import { FC, useMemo } from 'react'

import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import Clickable from 'core/components/lib/Clickable'
import ConfirmDialog from 'core/components/lib/ConfirmDialog'
import DynamicForm from 'core/components/lib/DynamicForm'
import type { SchemaField } from 'core/components/lib/DynamicForm/logic/schema'
import { Page } from 'core/components/lib/Page'
import PageHeader from 'core/components/lib/PageHeader'
import Spinner from 'core/components/lib/Spinner'
import { radiusCard, spaceCardPadding } from 'core/styles/dimensions'
import {
  error as errorToken,
  onWarningContainer,
  warningContainer,
} from 'core/styles/tokens'
import { ResourceCatalogEntry } from 'core/types/generated/resources'
import { StaticFieldSchema } from 'core/utils/generated/formSchemas'

import { JsonRecord } from './deriveColumns'
import deriveEditOmitFields from './deriveEditOmitFields'
import { dropMaskedSecrets, redactSecrets } from './redactSecrets'
import ResourceBackButton from './ResourceBackButton'
import { resourceBackFallbackPath } from './resourcePaths'
import useResourceNavigate from './useResourceNavigate'

/**
 * The read state of the record, however the caller obtained it. Matches what
 * the generated query hooks return, so a resource component can spread one.
 */
export type ResourceRecordResult = {
  data?: JsonRecord
  isLoading: boolean
  error: unknown
}

type ResourceDetailViewProps = ResourceRecordResult & {
  resource: ResourceCatalogEntry
  schema: Array<StaticFieldSchema>
  /** Corrections for fields the generated schema types wrongly. */
  fieldOverrides?: { [fieldPath: string]: SchemaField['component'] }
  /** The generated update-request schema. Present only where edit is wanted. */
  updateSchema?: Array<StaticFieldSchema>
  /** Sends the edited body. Given together with `updateSchema` to allow edit. */
  onSave?: (body: JsonRecord) => Promise<void>
  /** Deletes the record. The view navigates away once it resolves. */
  onDelete?: () => Promise<void>
}

const NO_OMIT_FIELDS: Array<string> = []

const ResourceDetailView: FC<ResourceDetailViewProps> = ({
  resource,
  schema,
  data,
  isLoading,
  error,
  fieldOverrides,
  updateSchema,
  onSave,
  onDelete,
}) => {
  const params = useParams<{ [param: string]: string }>()

  const record = useMemo(
    () => (data == null ? undefined : redactSecrets(data)),
    [data],
  )

  const canEdit = updateSchema != null && onSave != null

  /**
   * The API accepts an update but codegen produced no schema to build a form
   * from, so Edit is absent for a reason the page would otherwise hide. Say
   * so, rather than leave this looking like a resource nobody may edit.
   */
  const missingUpdateSchema =
    !canEdit && resource.operations.update?.hasBody === true

  const navigate = useResourceNavigate()

  const deleteAction =
    onDelete == null ? undefined : (
      <ConfirmDialog
        key='delete-action'
        onConfirm={async () => {
          await onDelete()
          navigate(
            resourceBackFallbackPath(resource.key, {
              companyId: params.companyId,
              hasList: resource.operations.list != null,
            }),
          )
        }}
        title={`Delete ${resource.label.singular}`}
      >
        <Clickable variant='outline'>Delete</Clickable>
      </ConfirmDialog>
    )

  const title =
    [record?.name, record?.id].find(
      (candidate): candidate is string => typeof candidate === 'string',
    ) ?? resource.label.singular

  return (
    <Page variant='full'>
      <PageHeader
        back={
          <ResourceBackButton
            fallbackPath={resourceBackFallbackPath(resource.key, {
              companyId: params.companyId,
              hasList: resource.operations.list != null,
            })}
          />
        }
        title={title}
      />
      {missingUpdateSchema ? (
        <NoticeMessage>
          Edit is unavailable: the API accepts an update for this resource, but
          no form schema was generated for its request body.
        </NoticeMessage>
      ) : null}
      {error != null ? (
        <ErrorMessage>
          {error instanceof Error ? error.message : 'Failed to load'}
        </ErrorMessage>
      ) : isLoading || record == null ? (
        <Spinner />
      ) : (
        <DynamicForm
          data={record}
          editSchema={canEdit ? updateSchema : undefined}
          fieldEditMode={canEdit ? 'toggle' : 'viewOnly'}
          fieldOverrides={fieldOverrides}
          omitFields={
            canEdit
              ? deriveEditOmitFields({
                  record,
                  responseSchema: schema,
                  updateSchema,
                })
              : NO_OMIT_FIELDS
          }
          onSubmit={
            canEdit
              ? async (payload) => {
                  await onSave(dropMaskedSecrets(payload as JsonRecord))
                }
              : undefined
          }
          otherActions={deleteAction == null ? undefined : [deleteAction]}
          schema={schema}
        />
      )}
    </Page>
  )
}

export default ResourceDetailView

const ErrorMessage = styled.p`
  color: ${(p) => p.theme.alert ?? errorToken};
  font-size: 14px;
`

const NoticeMessage = styled.p`
  border-radius: ${radiusCard};
  background: ${warningContainer};
  padding: ${spaceCardPadding};
  color: ${onWarningContainer};
  font-size: 14px;
`
